"use client";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./NewBlogs.css";
import Footer from "../../components/Footer/Footer";
import Navbar from "../../components/navbar/index.jsx";
import Container from "@mui/material/Container";
import { TextField, MenuItem, FormControl, InputLabel, Select, Pagination, Box } from "@mui/material";
import { CalendarOutlined, FolderOutlined, SearchOutlined } from "@ant-design/icons";
import aqtLogo from "../../assets/images/aqt.png";

const NewBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const blogsPerPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(
          "https://taiikitalks.com/api/get-posts/aqt"
        );
        
        // Sort blogs by date (newest first)
        const sortedBlogs = res.data.data.sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });
        
        setBlogs(sortedBlogs);
        setFilteredBlogs(sortedBlogs);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(sortedBlogs.map(blog => blog.category || "Uncategorized"))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // Filter blogs based on search and category
  useEffect(() => {
    let filtered = blogs;
    
    if (searchTerm) {
      filtered = filtered.filter(blog => 
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.summary.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory !== "all") {
      filtered = filtered.filter(blog => blog.category === selectedCategory);
    }
    
    setFilteredBlogs(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchTerm, selectedCategory, blogs]);

  // Get current blogs for pagination
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const pageCount = Math.ceil(filteredBlogs.length / blogsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top of blogs section
    document.querySelector('.new-blogs-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCardClick = (blogId) => {
    navigate(`/blog-detail/${blogId}`);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Check if a blog is the most recent (for "Latest" badge)
  const isMostRecentBlog = (blogId) => {
    if (filteredBlogs.length === 0) return false;
    // Get the most recent blog from the filtered list
    const mostRecentBlog = filteredBlogs.reduce((latest, current) => {
      return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
    }, filteredBlogs[0]);
    
    return mostRecentBlog.id === blogId;
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section - Matching FundRaise theme */}
      <div className="blogs-hero-section">
        <div className="blogs-hero-overlay"></div>
        <div className="blogs-hero-content">
          <h1 className="blogs-hero-title">Blogs</h1>
          <p className="blogs-hero-subtitle">
            Expert analysis on fundraising, capital markets, and financial strategy
          </p>
          <div className="blogs-hero-decoration"></div>
        </div>
      </div>

      <Container maxWidth="xl" className="blogs-main-container">
        <div className="new-blogs-container">
          
          {/* Search and Filter Section */}
          <div className="blogs-filter-section">
            <div className="search-container">
              <SearchOutlined className="search-icon" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="blog-search-input"
              />
            </div>
            
            <FormControl className="filter-select" size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Results Count */}
          <div className="blogs-results-count">
            <p>Showing {currentBlogs.length} of {filteredBlogs.length} articles</p>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="blogs-loading-container">
              <div className="loading-spinner"></div>
              <p>Loading insights...</p>
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="no-results-container">
              <div className="no-results-icon">📚</div>
              <h3>No articles found</h3>
              <p>Try adjusting your search or filter criteria</p>
              <button 
                className="reset-filters-btn"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCategory("all");
                }}
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              {/* Blogs Grid */}
              <div className="new-blogs-grid">
                {currentBlogs.map((blog, index) => (
                  <div
                    key={blog.id}
                    className={`new-blogs-card ${hoveredCard === blog.id ? 'hovered' : ''}`}
                    onMouseEnter={() => setHoveredCard(blog.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => handleCardClick(blog.id)}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Featured Badge - Show for the most recent blog only */}
                    {isMostRecentBlog(blog.id) && <span className="blog-badge">Latest</span>}
                    
                    <div className="blog-image-container">
                      <img
                        src={blog.image_mid ? `https://taiikitalks.com/${blog.image_mid}` : aqtLogo}
                        alt={blog.title}
                        className="new-blogs-image"
                        onError={(e) => { e.target.src = aqtLogo; }}
                      />
                      <div className="image-overlay"></div>
                    </div>
                    
                    <div className="blog-content">
                      {/* Category Tag */}
                      {blog.category && (
                        <span className="blog-category-tag">
                          <FolderOutlined className="category-icon" />
                          {blog.category}
                        </span>
                      )}
                      
                      <h2 className="new-blogs-heading">{blog.title}</h2>
                      
                      {/* Date if available */}
                      {blog.created_at && (
                        <div className="blog-date">
                          <CalendarOutlined className="date-icon" />
                          {formatDate(blog.created_at)}
                        </div>
                      )}
                      
                      <p className="new-blogs-summary">{blog.summary}</p>
                      
                      <div className="read-more-container">
                        <button
                          className="new-blogs-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/blog-detail/${blog.id}`);
                          }}
                        >
                          Read Article
                          <span className="button-arrow">→</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pageCount > 1 && (
                <Box className="pagination-container">
                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#0f766e',
                        '&.Mui-selected': {
                          backgroundColor: '#0f766e',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#0b837a',
                          }
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(15, 118, 110, 0.1)',
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </div>
      </Container>
      
      
      <Footer />
    </>
  );
};

export default NewBlogs;